/**
 * "Row colors": a person's own rules for tinting queue rows (087).
 *
 * "Show Peter's tickets in pink", "anything from the CFO in orange", "the
 * VPN tag in teal". Each rule is a field, a value and a color; the first
 * rule a row matches gives it its color, so the order is the priority and
 * can be changed. Only the person who sets them sees them.
 *
 * Values are picked from what the desk has -- its people, queues, types --
 * so a rule cannot be written against a name nobody has. A requester and a
 * tag are typed, because neither has a list worth scrolling.
 */
import { useState } from 'react';
import { ArrowDown, ArrowUp, Palette, Plus, Trash2 } from 'lucide-react';
import {
  MAX_COLOR_RULES,
  ROW_COLORS,
  type QueuePrefs,
  type RowColor,
  type RowColorField,
  type RowColorRule,
} from '@esa/shared';
import type { TenantConfig } from '../lib/api.js';
import { personLabel } from '../lib/format.js';
import { ROW_COLOR_HEX, ROW_COLOR_LABEL } from '../lib/rowColors.js';
import { Button, Dialog, DialogContent, Input, PRIORITY_OPTIONS, Select, Tooltip, cn } from '../ui/index.js';

const FIELD_LABEL: Record<RowColorField, string> = {
  assignee: 'Owner is',
  requester: 'Requester email is',
  queue: 'Queue is',
  requestType: 'Type is',
  priority: 'Priority is',
  tag: 'Tagged',
};

const newId = () => Math.random().toString(36).slice(2, 10);

function valueOptions(field: RowColorField, cfg: TenantConfig | undefined): { value: string; label: string }[] | null {
  if (field === 'assignee') return (cfg?.agents ?? []).map((a) => ({ value: a.oid, label: personLabel(a.name, a.email) }));
  if (field === 'queue') return (cfg?.queues ?? []).map((q) => ({ value: q.key, label: q.name }));
  if (field === 'requestType') return (cfg?.requestTypes ?? []).map((t) => ({ value: t.label, label: t.label }));
  if (field === 'priority') return PRIORITY_OPTIONS.map((p) => ({ value: p.value, label: p.label }));
  return null;
}

export function RowColors({
  prefs,
  cfg,
  onSave,
  saving,
}: {
  prefs: QueuePrefs;
  cfg: TenantConfig | undefined;
  onSave: (next: QueuePrefs) => void;
  saving: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [rules, setRules] = useState<RowColorRule[]>([]);

  const begin = (o: boolean) => {
    if (o) setRules(prefs.colors);
    setOpen(o);
  };
  const update = (i: number, patch: Partial<RowColorRule>) =>
    setRules((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const move = (i: number, by: -1 | 1) =>
    setRules((rs) => {
      const next = [...rs];
      const [r] = next.splice(i, 1);
      next.splice(i + by, 0, r!);
      return next;
    });
  // A rule with no value yet matches nothing and cannot be saved; it is
  // dropped rather than refusing the whole list over it.
  const complete = rules.filter((r) => r.value.trim() !== '');

  return (
    <Dialog open={open} onOpenChange={begin}>
      <Tooltip content="Row colors">
        <Button
          variant="secondary"
          size="sm"
          iconOnly
          aria-label="Row colors"
          onClick={() => begin(true)}
          className={cn(prefs.colors.length > 0 && 'text-brand')}
        >
          <Palette className="h-3.5 w-3.5" />
        </Button>
      </Tooltip>
      <DialogContent
        title="Row colors"
        description="Give rows a color by who owns them, who asked, the queue, type, priority or a tag. The first rule a row matches wins. Only you see these."
        width="lg"
      >
        <div className="space-y-2">
          {rules.length === 0 && <p className="text-xs text-ink-tertiary">No colors yet.</p>}
          {rules.map((r, i) => {
            const options = valueOptions(r.field, cfg);
            return (
              <div key={r.id} className="flex flex-wrap items-center gap-2 rounded-md border border-line-subtle p-2">
                <Select
                  size="sm"
                  aria-label="When"
                  value={r.field}
                  className="w-[10rem]"
                  onValueChange={(v) => update(i, { field: v as RowColorField, value: '' })}
                  options={Object.entries(FIELD_LABEL).map(([value, label]) => ({ value, label }))}
                />
                {options ? (
                  <Select
                    size="sm"
                    aria-label="Value"
                    value={r.value}
                    placeholder="Choose…"
                    className="min-w-[10rem] flex-1"
                    onValueChange={(v) => update(i, { value: v })}
                    options={options}
                  />
                ) : (
                  <Input
                    aria-label="Value"
                    className="h-8 min-w-[10rem] flex-1"
                    value={r.value}
                    maxLength={200}
                    placeholder={r.field === 'requester' ? 'name@esassoc.com' : 'tag'}
                    onChange={(e) => update(i, { value: e.target.value })}
                  />
                )}
                <div role="radiogroup" aria-label="Color" className="flex items-center gap-1">
                  {ROW_COLORS.map((c: RowColor) => (
                    <button
                      key={c}
                      type="button"
                      role="radio"
                      aria-checked={r.color === c}
                      aria-label={ROW_COLOR_LABEL[c]}
                      title={ROW_COLOR_LABEL[c]}
                      onClick={() => update(i, { color: c })}
                      className={cn(
                        'h-5 w-5 rounded-full ring-offset-2 ring-offset-surface transition-shadow',
                        r.color === c ? 'ring-2 ring-ink' : 'hover:ring-1 hover:ring-line',
                      )}
                      style={{ background: ROW_COLOR_HEX[c] }}
                    />
                  ))}
                </div>
                <div className="ml-auto flex items-center">
                  <Button size="sm" variant="ghost" iconOnly aria-label="Move up" disabled={i === 0} onClick={() => move(i, -1)}>
                    <ArrowUp className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    iconOnly
                    aria-label="Move down"
                    disabled={i === rules.length - 1}
                    onClick={() => move(i, 1)}
                  >
                    <ArrowDown className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    iconOnly
                    aria-label="Remove"
                    onClick={() => setRules((rs) => rs.filter((_, j) => j !== i))}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
          <Button
            size="sm"
            variant="secondary"
            leading={<Plus className="h-3.5 w-3.5" />}
            disabled={rules.length >= MAX_COLOR_RULES}
            onClick={() => setRules((rs) => [...rs, { id: newId(), field: 'assignee', value: '', color: 'pink' }])}
          >
            Add a color
          </Button>
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            loading={saving}
            onClick={() => {
              onSave({ ...prefs, colors: complete });
              setOpen(false);
            }}
          >
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
